import SectionHeader from "@/components/feature/panel/SectionHeader";
import EmptyState from "@/components/feature/panel/EmptyState";
import PanelLinks from "@/components/feature/panel/PanelLinks";
import ChildSelector from "@/pages/veli/components/ChildSelector";
import { useVeliChild, useChildAiAnalysis } from "@/hooks/useVeli";
import {useSiteSettingsContext} from "@/hooks/useSiteSettingsContext";

export default function AiAnaliz() {
  const { settings } = useSiteSettingsContext();
  const name = settings.siteName || "DersDinle";
  const { children, activeChild, activeUserId, setActiveUserId, loading: childrenLoading } =
    useVeliChild();
  const { data, loading, error, reload } = useChildAiAnalysis(activeChild?.userId ?? null);

  if (childrenLoading) return <EmptyState icon="ri-loader-4-line" title="Yükleniyor..." />;

  if (children.length === 0) {
    return (
      <div className="space-y-6">
        <SectionHeader
          title="AI Öğrenci Analizi"
          description="Yapay zekâ tarafından hazırlanan haftalık gelişim özeti ve öneriler."
        />
        <EmptyState
          icon="ri-brain-line"
          title="Henüz öğrenci hesabı bağlanmadı"
          description="Çocuğunuzun hesabını eşleştirdiğinizde AI analizi burada görünecek."
        />
        <PanelLinks
          links={[{ label: "Çocuğunu Eşleştir", href: "/veli/profil", icon: "ri-link" }]}
        />
      </div>
    );
  }

  const analysis = data;

  return (
    <div className="space-y-6">
      <SectionHeader
        title="AI Öğrenci Analizi"
        description={`${name} Asistan'ın ${activeChild?.fullName ?? "öğrenciniz"} için hazırladığı analiz ve öneriler.`}
      />
      <ChildSelector
        children={children}
        activeUserId={activeUserId}
        onSelect={setActiveUserId}
      />

      {loading ? (
        <EmptyState icon="ri-loader-4-line" title="Yükleniyor..." />
      ) : error ? (
        <div className="rounded-lg border border-background-200 bg-background-50 p-6 text-center">
          <p className="text-sm text-foreground-600">{error}</p>
          <button
            type="button"
            onClick={reload}
            className="mt-4 inline-flex items-center gap-2 whitespace-nowrap px-4 py-2 rounded-md text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
          >
            <i className="ri-refresh-line" aria-hidden="true"></i>
            Tekrar dene
          </button>
        </div>
      ) : !analysis ? (
        <EmptyState
          icon="ri-brain-line"
          title="Henüz analiz yok"
          description={`Çocuğunuz ${name} Asistan'ı kullandıkça analiz ve öneriler burada görünecek.`}
        />
      ) : !analysis.summary &&
        analysis.strongSubjects.length === 0 &&
        analysis.weakTopics.length === 0 ? (
        <EmptyState
          icon="ri-brain-line"
          title="Henüz yeterli veri yok"
          description={`Çocuğunuz ${name} Asistan'ı kullandıkça ders bazlı başarı ve kişisel öneriler burada görünecek.`}
        />
      ) : (
        <>
          {/* AI özeti */}
          {analysis.summary && (
            <div className="rounded-lg border border-background-200 bg-background-50 p-6">
              <p className="flex items-center gap-2 text-sm font-semibold text-foreground-900">
                <i className="ri-brain-line text-primary-600" aria-hidden="true"></i>
                AI Değerlendirmesi
              </p>
              <p className="mt-3 text-sm text-foreground-700 leading-relaxed whitespace-pre-line">
                {analysis.summary}
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Güçlü ve zayıf yönler */}
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading font-semibold text-base text-foreground-950">
                Güçlü Yönler
              </h2>
              {analysis.strongSubjects.length === 0 ? (
                <p className="mt-3 text-sm text-foreground-500">Henüz belirlenmedi.</p>
              ) : (
                <ul className="mt-3 space-y-2">
                  {analysis.strongSubjects.map((s) => (
                    <li
                      key={s}
                      className="flex items-center gap-2.5 rounded-md bg-accent-100/60 px-3 py-2.5 text-sm font-medium text-accent-800"
                    >
                      <i className="ri-checkbox-circle-line" aria-hidden="true"></i>
                      {s}
                    </li>
                  ))}
                </ul>
              )}

              <h2 className="mt-5 font-heading font-semibold text-base text-foreground-950">
                Destek Gereken Alanlar
              </h2>
              {analysis.weakSubjects.length === 0 && analysis.weakTopics.length === 0 ? (
                <p className="mt-3 text-sm text-foreground-500">Henüz belirlenmedi.</p>
              ) : (
                <>
                  {analysis.weakSubjects.length > 0 && (
                    <ul className="mt-3 space-y-2">
                      {analysis.weakSubjects.map((s) => (
                        <li
                          key={s}
                          className="flex items-center gap-2.5 rounded-md bg-secondary-100/60 px-3 py-2.5 text-sm font-medium text-secondary-900"
                        >
                          <i className="ri-alert-line" aria-hidden="true"></i>
                          {s}
                        </li>
                      ))}
                    </ul>
                  )}
                  {analysis.weakTopics.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {analysis.weakTopics.map((t) => (
                        <div
                          key={t.topic}
                          className="flex items-center justify-between gap-3 rounded-md border border-background-200 bg-background-100/50 px-3 py-2.5"
                        >
                          <span className="text-sm text-foreground-800">{t.topic}</span>
                          <span className="text-xs font-semibold text-foreground-600 whitespace-nowrap">
                            %{t.mastery}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Profil ve son çalışmalar */}
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading font-semibold text-base text-foreground-950">
                Öğrenme Profili
              </h2>
              <dl className="mt-3 space-y-2 text-sm">
                {analysis.learningLevel && (
                  <div className="flex items-center justify-between gap-3">
                    <dt className="text-foreground-500">Seviye</dt>
                    <dd className="font-medium text-foreground-900">{analysis.learningLevel}</dd>
                  </div>
                )}
                {analysis.motivationLevel > 0 && (
                  <div className="flex items-center justify-between gap-3">
                    <dt className="text-foreground-500">Motivasyon</dt>
                    <dd className="font-medium text-foreground-900">{analysis.motivationLevel}/10</dd>
                  </div>
                )}
              </dl>

              {analysis.recentNotes.length > 0 && (
                <>
                  <h2 className="mt-6 font-heading font-semibold text-base text-foreground-950">
                    Son Çalışmalar
                  </h2>
                  <ul className="mt-3 space-y-2">
                    {analysis.recentNotes.slice(0, 5).map((n, i) => (
                      <li
                        key={i}
                        className="text-sm text-foreground-600 rounded-md bg-background-100/60 px-3 py-2.5 leading-relaxed"
                      >
                        {n}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </div>
        </>
      )}

      <PanelLinks
        links={[
          { label: "Haftalık Rapor", href: "/veli/haftalik-rapor", icon: "ri-bar-chart-line" },
          { label: "Deneme Sonuçları", href: "/veli/deneme-sonuclari", icon: "ri-file-text-line" },
        ]}
      />
    </div>
  );
}